import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Home, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { navigateBack } from '../utils/navigation';

const AccessDenied = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const getRoleDashboard = () => {
    if (!user) return '/login';
    
    switch (user.role) {
      case 'admin':
        return '/admin/dashboard';
      case 'college_admin':
        return '/college/dashboard';
      case 'student':
        return '/student/dashboard';
      default:
        return '/dashboard';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
            <ShieldAlert className="w-10 h-10 text-red-600" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-lg text-gray-600 mb-6">403 - Forbidden</p>

          {/* Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800">
              You don't have permission to access this page. This area is restricted to specific user roles.
            </p>
          </div>

          {/* User Info */}
          {user && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Current User</p>
              <p className="text-sm font-semibold text-gray-900">{user.fullName}</p>
              <p className="text-xs text-gray-600 capitalize mt-1">Role: {user.role?.replace('_', ' ')}</p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => navigate(getRoleDashboard())}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-600 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-700 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Home className="w-5 h-5" />
              <span>Go to My Dashboard</span>
            </button>

            <button
              onClick={() => navigateBack(navigate, getRoleDashboard())}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Go Back</span>
            </button>

            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="w-full px-6 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Sign out and login as different user
            </button>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          If you believe this is an error, please contact your system administrator.
        </p>
      </div>
    </div>
  );
};

export default AccessDenied;
